// src/components/SettingsView.js
import React, { useEffect, useState } from "react";
import {
  getProviderKeys,
  getProviderKeysStatus,
  saveProviderKeys,
  getSettings,
  saveSettings,
  getReadinessAlertPolicy,
  getReadinessActionPolicy,
  getRiskPolicy,
  saveReadinessAlertPolicy,
  saveReadinessActionPolicy,
  saveRiskPolicy,
  getBrokerSyncPolicy,
  saveBrokerSyncPolicy,
  getBrokerSyncStatus,
  getPortfolioEquitySeedStatus,
  runBinancePreflight,
  runAlpacaPreflight,
  armAlpacaLiveTrading,
  getAlpacaLiveArmStatus,
  submitAlpacaGuardedOrder,
  getExecutionPolicy,
  saveExecutionPolicy,
  getEmailAlertPolicy,
  saveEmailAlertPolicy,
  getRotationPolicy,
  saveRotationPolicy,
  getProviderBudgetPolicy,
  getAppSurfacePolicy,
  saveProviderBudgetPolicy,
  getSleevePolicy,
  saveSleevePolicy,
  getAutonomousExecPolicy,
  saveAutonomousExecPolicy,
  armAutonomousEngine,
  disarmAutonomousEngine,
  getAutonomousStatus,
  getAutonomousLog,
  exportAutonomousLogCsv,
} from "../api/settings";
import {
  clearStoredEntitlement,
  getCapabilities,
  getEntitlementStoreStatus,
  validateLicenseAndStoreEntitlement,
} from "../api/capabilities";
import { Alert, Box, Button, Card, CardContent, Chip, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import ResponsiveContainer from "./ResponsiveContainer";

const PROVIDER_KEY_FIELDS = [
  {
    name: "alpaca",
    label: "Alpaca API Key",
    helperText: "Used for broker equity and order sync.",
  },
  {
    name: "alpaca_secret",
    label: "Alpaca Secret Key",
    helperText: "Required with Alpaca API Key for authenticated broker sync.",
  },
  {
    name: "smtp_password",
    label: "SMTP / Gmail App Password",
    helperText: "App password for Gmail SMTP email alerts (not your account password).",
    section: "email",
  },
  {
    name: "fmp",
    label: "FMP API Key",
    helperText: "Financial Modeling Prep market/fundamental data.",
  },
  {
    name: "fred",
    label: "FRED API Key",
    helperText: "Federal Reserve economic series data.",
  },
  {
    name: "alphavantage",
    label: "Alpha Vantage API Key",
    helperText: "Fallback market data provider.",
  },
  {
    name: "polygon",
    label: "Polygon API Key",
    helperText: "Optional premium market data provider.",
  },
  {
    name: "finnhub",
    label: "Finnhub API Key",
    helperText: "Optional quote/news enrichment provider.",
  },
  {
    name: "twelvedata",
    label: "Twelve Data API Key",
    helperText: "Optional alternate market data provider.",
  },
];

const SettingsView = () => {
  const [providerKeys, setProviderKeys] = useState({});
  const [providerKeyStatus, setProviderKeyStatus] = useState({});
  const [settings, setSettings] = useState({});
  const [riskPolicy, setRiskPolicy] = useState({});
  const [readinessAlertPolicy, setReadinessAlertPolicy] = useState({});
  const [readinessActionPolicy, setReadinessActionPolicy] = useState({});
  const [brokerSyncPolicy, setBrokerSyncPolicy] = useState({});
  const [brokerSyncStatus, setBrokerSyncStatus] = useState(null);
  const [portfolioSeedStatus, setPortfolioSeedStatus] = useState(null);
  const [binancePreflightLoading, setBinancePreflightLoading] = useState(false);
  const [binancePreflightResult, setBinancePreflightResult] = useState(null);
  const [alpacaPreflightLoading, setAlpacaPreflightLoading] = useState(false);
  const [alpacaPreflightResult, setAlpacaPreflightResult] = useState(null);
  const [alpacaLiveArmLoading, setAlpacaLiveArmLoading] = useState(false);
  const [alpacaLiveArmStatus, setAlpacaLiveArmStatus] = useState(null);
  const [alpacaLiveArmPassphrase, setAlpacaLiveArmPassphrase] = useState("");
  const [alpacaLiveArmTtl, setAlpacaLiveArmTtl] = useState(15);
  const [alpacaGuardedLoading, setAlpacaGuardedLoading] = useState(false);
  const [alpacaGuardedResult, setAlpacaGuardedResult] = useState(null);
  const [alpacaGuardedTicker, setAlpacaGuardedTicker] = useState("AAPL");
  const [alpacaGuardedQty, setAlpacaGuardedQty] = useState(1);
  const [alpacaGuardedSide, setAlpacaGuardedSide] = useState("buy");
  const [executionPolicy, setExecutionPolicy] = useState({});
  const [emailAlertPolicy, setEmailAlertPolicy] = useState({});
  const [rotationPolicy, setRotationPolicy] = useState({});
  const [providerBudgetPolicy, setProviderBudgetPolicy] = useState({});
  const [appSurfacePolicy, setAppSurfacePolicy] = useState({});
  const [sleevePolicy, setSleevePolicy] = useState({});
  const [autonomousExecPolicy, setAutonomousExecPolicy] = useState({});
  const [autonomousStatus, setAutonomousStatus] = useState(null);
  const [autonomousLogPreview, setAutonomousLogPreview] = useState([]);
  const [armReason, setArmReason] = useState("");
  const [armLoading, setArmLoading] = useState(false);
  const [armError, setArmError] = useState("");
  const [armSuccess, setArmSuccess] = useState("");
  const [governanceExportLoading, setGovernanceExportLoading] = useState(false);
  const [governanceExportError, setGovernanceExportError] = useState("");
  const [governanceExportSuccess, setGovernanceExportSuccess] = useState("");
  const [actionsPreviewFilter, setActionsPreviewFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseMessage, setLicenseMessage] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const [capabilitySnapshot, setCapabilitySnapshot] = useState(null);
  const [entitlementStoreSnapshot, setEntitlementStoreSnapshot] = useState(null);

  // Retry logic
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError("");
    const safeStatusCall = () => {
      try {
        const p = getBrokerSyncStatus();
        return (p && typeof p.then === "function") ? p.catch(() => null) : Promise.resolve(null);
      } catch {
        return Promise.resolve(null);
      }
    };

    Promise.all([
      getProviderKeys(),
      getProviderKeysStatus(),
      getSettings(),
      getRiskPolicy(),
      getReadinessAlertPolicy(),
      getReadinessActionPolicy(),
      getBrokerSyncPolicy(),
      safeStatusCall(),
      Promise.resolve().then(() => getPortfolioEquitySeedStatus()).catch(() => null),
      Promise.resolve().then(() => getExecutionPolicy()).catch(() => ({})),
      Promise.resolve().then(() => getEmailAlertPolicy()).catch(() => ({})),
      Promise.resolve().then(() => getRotationPolicy()).catch(() => ({})),
      Promise.resolve().then(() => getProviderBudgetPolicy()).catch(() => ({})),
      Promise.resolve().then(() => getAppSurfacePolicy()).catch(() => ({})),
      Promise.resolve().then(() => getCapabilities()).catch(() => null),
      Promise.resolve().then(() => getEntitlementStoreStatus()).catch(() => null),
      Promise.resolve().then(() => getSleevePolicy()).catch(() => ({})),
      Promise.resolve().then(() => getAutonomousExecPolicy()).catch(() => ({})),
      Promise.resolve().then(() => getAutonomousStatus()).catch(() => null),
      Promise.resolve().then(() => getAutonomousLog({ limit: 5 })).catch(() => ({ runs: [] })),
    ])
      .then((
        [
          keys,
          keyStatus,
          loadedSettings,
          loadedRiskPolicy,
          loadedReadinessAlertPolicy,
          loadedReadinessActionPolicy,
          loadedBrokerSyncPolicy,
          loadedSyncStatus,
          loadedPortfolioSeedStatus,
          loadedExecutionPolicy,
          loadedEmailAlertPolicy,
          loadedRotationPolicy,
          loadedProviderBudgetPolicy,
          loadedAppSurfacePolicy,
          loadedCapabilities,
          loadedEntitlementStoreStatus,
          loadedSleevePolicy,
          loadedAutonomousExecPolicy,
          loadedAutonomousStatus,
          loadedAutonomousLog,
        ]
      ) => {
        const normalizedKeys = {
          ...Object.fromEntries(PROVIDER_KEY_FIELDS.map((field) => [field.name, ""])),
          ...(keys && typeof keys === "object" ? keys : {}),
        };
        setProviderKeys(normalizedKeys);
        setProviderKeyStatus(keyStatus && typeof keyStatus === "object" ? keyStatus : {});
        setSettings(loadedSettings && typeof loadedSettings === "object" ? loadedSettings : {});
        setRiskPolicy(loadedRiskPolicy && typeof loadedRiskPolicy === "object" ? loadedRiskPolicy : {});
        setReadinessAlertPolicy(
          loadedReadinessAlertPolicy && typeof loadedReadinessAlertPolicy === "object"
            ? loadedReadinessAlertPolicy
            : {}
        );
        setReadinessActionPolicy(
          loadedReadinessActionPolicy && typeof loadedReadinessActionPolicy === "object"
            ? loadedReadinessActionPolicy
            : {}
        );
        setBrokerSyncPolicy(
          loadedBrokerSyncPolicy && typeof loadedBrokerSyncPolicy === "object"
            ? loadedBrokerSyncPolicy
            : {}
        );
        setBrokerSyncStatus(
          loadedSyncStatus && typeof loadedSyncStatus === "object" ? loadedSyncStatus : null
        );
        setPortfolioSeedStatus(
          loadedPortfolioSeedStatus && typeof loadedPortfolioSeedStatus === "object"
            ? loadedPortfolioSeedStatus
            : null
        );
        setExecutionPolicy(loadedExecutionPolicy && typeof loadedExecutionPolicy === "object" ? loadedExecutionPolicy : {});
        setEmailAlertPolicy(loadedEmailAlertPolicy && typeof loadedEmailAlertPolicy === "object" ? loadedEmailAlertPolicy : {});
        setRotationPolicy(loadedRotationPolicy && typeof loadedRotationPolicy === "object" ? loadedRotationPolicy : {});
        setProviderBudgetPolicy(
          loadedProviderBudgetPolicy && typeof loadedProviderBudgetPolicy === "object"
            ? loadedProviderBudgetPolicy
            : {}
        );
        setAppSurfacePolicy(loadedAppSurfacePolicy && typeof loadedAppSurfacePolicy === "object" ? loadedAppSurfacePolicy : {});
        setCapabilitySnapshot(loadedCapabilities && typeof loadedCapabilities === "object" ? loadedCapabilities : null);
        const normalizedStore = loadedEntitlementStoreStatus?.desktop_entitlement_store;
        setEntitlementStoreSnapshot(normalizedStore && typeof normalizedStore === "object" ? normalizedStore : null);
        setSleevePolicy(loadedSleevePolicy && typeof loadedSleevePolicy === "object" ? loadedSleevePolicy : {});
        setAutonomousExecPolicy(loadedAutonomousExecPolicy && typeof loadedAutonomousExecPolicy === "object" ? loadedAutonomousExecPolicy : {});
        setAutonomousStatus(loadedAutonomousStatus && typeof loadedAutonomousStatus === "object" ? loadedAutonomousStatus : null);
        const logRuns = Array.isArray(loadedAutonomousLog?.runs) ? loadedAutonomousLog.runs : [];
        setAutonomousLogPreview(logRuns);
        getAlpacaLiveArmStatus()
          .then((statusPayload) => {
            setAlpacaLiveArmStatus(statusPayload && typeof statusPayload === "object" ? statusPayload : null);
          })
          .catch(() => {
            setAlpacaLiveArmStatus(null);
          });
      })
      .catch(() => setError("Unable to load settings. Please check your connection or try again."))
      .finally(() => setLoading(false));
  }, [retryCount]);

  // Input validation state
  const [inputErrors, setInputErrors] = useState({});
  const safeProviderKeys = providerKeys && typeof providerKeys === "object" ? providerKeys : {};
  const safeProviderKeyStatus = providerKeyStatus && typeof providerKeyStatus === "object" ? providerKeyStatus : {};
  const safeSettings = settings && typeof settings === "object" ? settings : {};
  const safeRiskPolicy = riskPolicy && typeof riskPolicy === "object" ? riskPolicy : {};
  const safeReadinessAlertPolicy =
    readinessAlertPolicy && typeof readinessAlertPolicy === "object" ? readinessAlertPolicy : {};
  const safeReadinessActionPolicy =
    readinessActionPolicy && typeof readinessActionPolicy === "object" ? readinessActionPolicy : {};
  const safeBrokerSyncPolicy =
    brokerSyncPolicy && typeof brokerSyncPolicy === "object" ? brokerSyncPolicy : {};
  const safeExecutionPolicy =
    executionPolicy && typeof executionPolicy === "object" ? executionPolicy : {};
  const safeEmailAlertPolicy =
    emailAlertPolicy && typeof emailAlertPolicy === "object" ? emailAlertPolicy : {};
  const safeRotationPolicy =
    rotationPolicy && typeof rotationPolicy === "object" ? rotationPolicy : {};
  const safeProviderBudgetPolicy =
    providerBudgetPolicy && typeof providerBudgetPolicy === "object" ? providerBudgetPolicy : {};
  const safeAppSurfacePolicy =
    appSurfacePolicy && typeof appSurfacePolicy === "object" ? appSurfacePolicy : {};
  const safeLegalLinks =
    safeAppSurfacePolicy.legal_links && typeof safeAppSurfacePolicy.legal_links === "object"
      ? safeAppSurfacePolicy.legal_links
      : {};
  const safeOpsBaseline =
    safeAppSurfacePolicy.ops_baseline && typeof safeAppSurfacePolicy.ops_baseline === "object"
      ? safeAppSurfacePolicy.ops_baseline
      : {};
  const safeMonitoringBaseline =
    safeOpsBaseline.monitoring && typeof safeOpsBaseline.monitoring === "object"
      ? safeOpsBaseline.monitoring
      : {};
  const safeSleevePolicy =
    sleevePolicy && typeof sleevePolicy === "object" ? sleevePolicy : {};
  const safeAutonomousExecPolicy =
    autonomousExecPolicy && typeof autonomousExecPolicy === "object" ? autonomousExecPolicy : {};
  const isArmed = Boolean(safeAutonomousExecPolicy.armed ?? autonomousStatus?.armed);
  const governanceRunsTotal = Number(autonomousStatus?.runs_total ?? 0);
  const governanceActionsTotal = Number(autonomousStatus?.total_actions_logged ?? 0);
  const governanceRunsLive = Number(autonomousStatus?.runs_live ?? 0);
  const governanceLastRunAt = autonomousStatus?.last_run_at || null;
  const governanceLastRunError = autonomousStatus?.last_run_error || null;
  const governanceRunCapacity = Number(autonomousStatus?.log_run_capacity ?? 0);
  const governanceActionCapacity = Number(autonomousStatus?.log_action_capacity ?? 0);
  const governanceUsageKnown = governanceRunCapacity > 0 && governanceActionCapacity > 0;
  const governanceNearCapacity = Boolean(autonomousStatus?.log_retention_near_capacity);
  const recentActionsPreview = (Array.isArray(autonomousLogPreview) ? autonomousLogPreview : [])
    .flatMap((run) => {
      const runActions = Array.isArray(run?.actions) ? run.actions : [];
      return runActions.map((action) => ({
        ranAt: run?.ran_at || "",
        actionType: action?.action_type || "",
        symbol: action?.symbol || "",
        rationale: action?.rationale || "",
      }));
    })
    .slice(0, 5);
  const filteredActionsPreview = recentActionsPreview.filter((action) => {
    const actionType = String(action?.actionType || "").toUpperCase();
    const isExit = actionType.startsWith("EXIT") || actionType.includes("HARVEST") || actionType.includes("WRITE_OFF");
    const isEntry = actionType.startsWith("ENTER");
    if (actionsPreviewFilter === "entries") return isEntry;
    if (actionsPreviewFilter === "exits") return isExit;
    return true;
  });
  const hasBrokerSync = Boolean(capabilitySnapshot?.capabilities?.broker_sync ?? true);
  const hasPaperTrading = Boolean(capabilitySnapshot?.capabilities?.paper_trading ?? true);
  const hasAutomationEngine = Boolean(capabilitySnapshot?.capabilities?.automation_engine ?? true);
  const activeTierLabel = capabilitySnapshot?.active_tier_label || "TradePilot Executive";
  const baseTierLabel = capabilitySnapshot?.base_tier_label || activeTierLabel;
  const entitlementApplied = Boolean(capabilitySnapshot?.entitlement_applied);
  const entitlementStoreMode = String(entitlementStoreSnapshot?.mode || "unknown").trim() || "unknown";
  const entitlementStoreTokenPresent = Boolean(entitlementStoreSnapshot?.token_present);

  const secureStoreModeHelper = {
    windows_credential_manager: "Uses Windows Credential Manager for desktop entitlement persistence.",
    unavailable: "Secure desktop storage is not available here; local token fallback is active.",
    disabled: "Secure desktop storage is disabled by configuration (TRADEPILOT_DESKTOP_SECURE_STORE).",
    unknown: "Secure-store mode could not be determined for this runtime.",
  };
  const secureStoreHelperText = secureStoreModeHelper[entitlementStoreMode] || secureStoreModeHelper.unknown;

  const handleKeyChange = (e) => {
    setProviderKeys({ ...safeProviderKeys, [e.target.name]: e.target.value });
  };

  const handleExecutionPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setExecutionPolicy({ ...safeExecutionPolicy, [e.target.name]: nextValue });
  };

  const handleEmailAlertPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setEmailAlertPolicy({ ...safeEmailAlertPolicy, [e.target.name]: nextValue });
  };

  const handleRotationPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setRotationPolicy({ ...safeRotationPolicy, [e.target.name]: nextValue });
  };

  const handleSleevePolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSleevePolicy({ ...safeSleevePolicy, [e.target.name]: nextValue });
  };

  const handleAutonomousExecPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setAutonomousExecPolicy({ ...safeAutonomousExecPolicy, [e.target.name]: nextValue });
  };

  const handleArmEngine = async () => {
    if (!hasAutomationEngine) {
      setArmError("Executive tier is required to arm the autonomous engine.");
      return;
    }
    if (!armReason.trim()) {
      setArmError("Please enter a reason before arming.");
      return;
    }
    setArmLoading(true);
    setArmError("");
    setArmSuccess("");
    try {
      await armAutonomousEngine(armReason.trim());
      setArmSuccess("Engine armed successfully.");
      setArmReason("");
      const latest = await getAutonomousExecPolicy().catch(() => ({}));
      setAutonomousExecPolicy(latest && typeof latest === "object" ? latest : {});
      const latestStatus = await getAutonomousStatus().catch(() => null);
      setAutonomousStatus(latestStatus);
      const latestLog = await getAutonomousLog({ limit: 5 }).catch(() => ({ runs: [] }));
      setAutonomousLogPreview(Array.isArray(latestLog?.runs) ? latestLog.runs : []);
    } catch (err) {
      setArmError(err?.message || "Failed to arm engine.");
    } finally {
      setArmLoading(false);
    }
  };

  const handleDisarmEngine = async () => {
    if (!hasAutomationEngine) {
      setArmError("Executive tier is required to manage autonomous engine state.");
      return;
    }
    setArmLoading(true);
    setArmError("");
    setArmSuccess("");
    try {
      await disarmAutonomousEngine();
      setArmSuccess("Engine disarmed.");
      const latest = await getAutonomousExecPolicy().catch(() => ({}));
      setAutonomousExecPolicy(latest && typeof latest === "object" ? latest : {});
      const latestStatus = await getAutonomousStatus().catch(() => null);
      setAutonomousStatus(latestStatus);
      const latestLog = await getAutonomousLog({ limit: 5 }).catch(() => ({ runs: [] }));
      setAutonomousLogPreview(Array.isArray(latestLog?.runs) ? latestLog.runs : []);
    } catch (err) {
      setArmError(err?.message || "Failed to disarm engine.");
    } finally {
      setArmLoading(false);
    }
  };

  const handleExportGovernanceCsv = async () => {
    if (!hasAutomationEngine) {
      setGovernanceExportError("Executive tier is required to export autonomous governance logs.");
      return;
    }
    setGovernanceExportLoading(true);
    setGovernanceExportError("");
    setGovernanceExportSuccess("");
    try {
      const csvPayload = await exportAutonomousLogCsv({ limit: 500 });
      const blob = new Blob([csvPayload], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tradepilot-autonomous-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setGovernanceExportSuccess("Governance log CSV downloaded.");
    } catch (err) {
      setGovernanceExportError(err?.message || "Failed to export governance log CSV.");
    } finally {
      setGovernanceExportLoading(false);
    }
  };

  const handleSettingsChange = (e) => {
    const nextValue = e.target.value;
    setSettings({ ...safeSettings, [e.target.name]: nextValue });
    if (String(nextValue).trim() === "") {
      setInputErrors((prev) => ({ ...prev, [e.target.name]: "Setting cannot be empty." }));
    } else {
      setInputErrors((prev) => {
        const { [e.target.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    // Validate all fields
    let errors = {};
    Object.entries(safeSettings).forEach(([k, v]) => {
      if (v === undefined || v === null || String(v).trim() === "") {
        errors[k] = "Setting cannot be empty.";
      }
    });
    Object.entries(safeRiskPolicy).forEach(([k, v]) => {
      if (v === undefined || v === null || String(v).trim() === "") {
        errors[k] = "Risk policy value cannot be empty.";
      }
    });
    Object.entries(safeReadinessAlertPolicy).forEach(([k, v]) => {
      if (k === "webhook_url") {
        return;
      }
      if (v === undefined || v === null || (typeof v === "string" && String(v).trim() === "")) {
        errors[k] = "Alert policy value cannot be empty.";
      }
    });
    Object.entries(safeBrokerSyncPolicy).forEach(([k, v]) => {
      if (v === undefined || v === null || String(v).trim() === "") {
        errors[k] = "Broker sync policy value cannot be empty.";
      }
    });
    Object.entries(safeReadinessActionPolicy).forEach(([k, v]) => {
      if (v === undefined || v === null || (typeof v === "string" && String(v).trim() === "")) {
        errors[k] = "Readiness action policy value cannot be empty.";
      }
    });
    Object.entries(safeProviderBudgetPolicy.active_plan || {}).forEach(([provider, plan]) => {
      if (!String(plan || "").trim()) {
        errors[`plan_${provider}`] = "Provider plan cannot be empty.";
      }
    });
    Object.entries(safeProviderBudgetPolicy.daily_calls || {}).forEach(([provider, limits]) => {
      const freeCalls = Number(limits?.free);
      const paidCalls = Number(limits?.paid);
      if (!Number.isFinite(freeCalls) || freeCalls < 1) {
        errors[`daily_calls_${provider}_free`] = "Free calls must be at least 1.";
      }
      if (!Number.isFinite(paidCalls) || paidCalls < 1) {
        errors[`daily_calls_${provider}_paid`] = "Paid calls must be at least 1.";
      }
    });
    setInputErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted errors before saving.");
      return;
    }
    try {
      const [
        savedKeys,
        savedSettings,
        savedRiskPolicyWrite,
        _savedReadinessAlertPolicyWrite,
        _savedReadinessActionPolicyWrite,
        savedBrokerSyncPolicyWrite,
        savedExecutionPolicyWrite,
        _savedEmailAlertPolicyWrite,
        _savedRotationPolicyWrite,
        _savedProviderBudgetPolicyWrite,
        _savedSleevePolicyWrite,
        savedAutonomousPolicyWrite,
      ] = await Promise.all([
        saveProviderKeys(safeProviderKeys),
        saveSettings(safeSettings),
        saveRiskPolicy({
          max_single_position_weight_pct: Number(safeRiskPolicy.max_single_position_weight_pct ?? 45),
          max_sleeve_weight_pct: Number(safeRiskPolicy.max_sleeve_weight_pct ?? 65),
          max_sector_weight_pct: Number(safeRiskPolicy.max_sector_weight_pct ?? 70),
          max_drawdown_pct: Number(safeRiskPolicy.max_drawdown_pct ?? 25),
        }),
        saveReadinessAlertPolicy({
          enabled: Boolean(safeReadinessAlertPolicy.enabled),
          min_repeat_minutes: Number(safeReadinessAlertPolicy.min_repeat_minutes),
          emit_on_recovery: Boolean(safeReadinessAlertPolicy.emit_on_recovery),
          webhook_url: safeReadinessAlertPolicy.webhook_url || "",
        }),
        saveReadinessActionPolicy({
          enabled: Boolean(safeReadinessActionPolicy.enabled),
          on_blocked: Boolean(safeReadinessActionPolicy.on_blocked),
          on_recovered: Boolean(safeReadinessActionPolicy.on_recovered),
          cooldown_minutes: Number(safeReadinessActionPolicy.cooldown_minutes),
          dry_run: Boolean(safeReadinessActionPolicy.dry_run),
        }),
        saveBrokerSyncPolicy({
          provider: String(safeBrokerSyncPolicy.provider || "alpaca"),
          binance_authenticated_enabled: Boolean(safeBrokerSyncPolicy.binance_authenticated_enabled),
          sync_days: Number(safeBrokerSyncPolicy.sync_days),
          retry_attempts: Number(safeBrokerSyncPolicy.retry_attempts),
          retry_backoff_seconds: Number(safeBrokerSyncPolicy.retry_backoff_seconds),
          min_interval_minutes: Number(safeBrokerSyncPolicy.min_interval_minutes),
          timeout_seconds: Number(safeBrokerSyncPolicy.timeout_seconds),
        }),
        saveExecutionPolicy({
          alpaca_base_url: safeExecutionPolicy.alpaca_base_url || "https://paper-api.alpaca.markets",
          paper_mode: Boolean(safeExecutionPolicy.paper_mode),
          dry_run: Boolean(safeExecutionPolicy.dry_run),
          order_type: safeExecutionPolicy.order_type || "market",
          time_in_force: safeExecutionPolicy.time_in_force || "day",
          order_timeout_seconds: Number(safeExecutionPolicy.order_timeout_seconds ?? 6),
        }),
        saveEmailAlertPolicy({
          enabled: Boolean(safeEmailAlertPolicy.enabled),
          smtp_host: safeEmailAlertPolicy.smtp_host || "smtp.gmail.com",
          smtp_port: Number(safeEmailAlertPolicy.smtp_port ?? 587),
          smtp_user: safeEmailAlertPolicy.smtp_user || "",
          smtp_to_address: safeEmailAlertPolicy.smtp_to_address || "",
        }),
        saveRotationPolicy({
          enable_rotation: Boolean(safeRotationPolicy.enable_rotation),
          min_candidate_score: Number(safeRotationPolicy.min_candidate_score ?? 55),
          min_score_edge: Number(safeRotationPolicy.min_score_edge ?? 15),
          max_positions: Number(safeRotationPolicy.max_positions ?? 20),
          profit_taking_enabled: Boolean(safeRotationPolicy.profit_taking_enabled),
        }),
        saveProviderBudgetPolicy({
          active_plan: { ...(safeProviderBudgetPolicy.active_plan || {}) },
          daily_calls: Object.fromEntries(
            Object.entries(safeProviderBudgetPolicy.daily_calls || {}).map(([provider, limits]) => [
              provider,
              {
                free: Math.max(1, Number(limits?.free || 1)),
                paid: Math.max(1, Number(limits?.paid || 1)),
              },
            ])
          ),
          market_window: {
            start: String(safeProviderBudgetPolicy.market_window?.start || "09:30"),
            end: String(safeProviderBudgetPolicy.market_window?.end || "16:00"),
            timezone: String(safeProviderBudgetPolicy.market_window?.timezone || "America/New_York"),
          },
        }),
        saveSleevePolicy({
          long_term_target_pct: Number(safeSleevePolicy.long_term_target_pct ?? 0.70),
          short_term_target_pct: Number(safeSleevePolicy.short_term_target_pct ?? 0.30),
          sleeve_tolerance_pct: Number(safeSleevePolicy.sleeve_tolerance_pct ?? 0.15),
          min_hold_days_long_term: Number(safeSleevePolicy.min_hold_days_long_term ?? 90),
          min_hold_days_short_term: Number(safeSleevePolicy.min_hold_days_short_term ?? 30),
          write_off_min_viable_score: Number(safeSleevePolicy.write_off_min_viable_score ?? 30),
          write_off_confirmation_cycles: Number(safeSleevePolicy.write_off_confirmation_cycles ?? 2),
          write_off_max_loss_pct: Number(safeSleevePolicy.write_off_max_loss_pct ?? -35),
        }),
        saveAutonomousExecPolicy({
          auto_exit_harvest: Boolean(safeAutonomousExecPolicy.auto_exit_harvest),
          auto_exit_write_off: Boolean(safeAutonomousExecPolicy.auto_exit_write_off),
          max_positions_per_run: Number(safeAutonomousExecPolicy.max_positions_per_run ?? 3),
          max_harvests_per_run: Number(safeAutonomousExecPolicy.max_harvests_per_run ?? 2),
          max_write_offs_per_run: Number(safeAutonomousExecPolicy.max_write_offs_per_run ?? 2),
          min_score_to_enter: Number(safeAutonomousExecPolicy.min_score_to_enter ?? 40),
          max_position_size_pct: Number(safeAutonomousExecPolicy.max_position_size_pct ?? 20),
          sleeve_mode: Boolean(safeAutonomousExecPolicy.sleeve_mode),
          include_prices_for_exit_scoring: Boolean(safeAutonomousExecPolicy.include_prices_for_exit_scoring),
          notify_on_action: Boolean(safeAutonomousExecPolicy.notify_on_action),
          notify_on_run_summary: Boolean(safeAutonomousExecPolicy.notify_on_run_summary),
          capital_source_mode: String(safeAutonomousExecPolicy.capital_source_mode || "max_available"),
          capital_safety_buffer_pct: Number(safeAutonomousExecPolicy.capital_safety_buffer_pct ?? 0),
        }),
      ]);

      const auditVisibilityEnabled = [
        savedRiskPolicyWrite,
        savedBrokerSyncPolicyWrite,
        savedExecutionPolicyWrite,
        savedAutonomousPolicyWrite,
      ].some((entry) => entry && typeof entry === "object" && entry.__audit_visibility === "enabled");
      if (savedKeys?.saved) {
        setProviderKeys(safeProviderKeys);
      }
      if (savedSettings) {
        setSettings(savedSettings);
      }
      const [
        savedPolicy,
        savedAlertPolicy,
        savedActionPolicy,
        savedBrokerPolicy,
        latestProviderKeyStatus,
        latestExecutionPolicy,
        latestEmailAlertPolicy,
        latestRotationPolicy,
        latestProviderBudgetPolicy,
        latestSleevePolicy,
        latestAutonomousExecPolicy,
      ] = await Promise.all([
        getRiskPolicy(),
        getReadinessAlertPolicy(),
        getReadinessActionPolicy(),
        getBrokerSyncPolicy(),
        getProviderKeysStatus(),
        Promise.resolve().then(() => getExecutionPolicy()).catch(() => ({})),
        Promise.resolve().then(() => getEmailAlertPolicy()).catch(() => ({})),
        Promise.resolve().then(() => getRotationPolicy()).catch(() => ({})),
        Promise.resolve().then(() => getProviderBudgetPolicy()).catch(() => ({})),
        Promise.resolve().then(() => getSleevePolicy()).catch(() => ({})),
        Promise.resolve().then(() => getAutonomousExecPolicy()).catch(() => ({})),
      ]);
      setRiskPolicy(savedPolicy);
      setReadinessAlertPolicy(savedAlertPolicy);
      setReadinessActionPolicy(savedActionPolicy && typeof savedActionPolicy === "object" ? savedActionPolicy : {});
      setBrokerSyncPolicy(savedBrokerPolicy && typeof savedBrokerPolicy === "object" ? savedBrokerPolicy : {});
      setProviderKeyStatus(latestProviderKeyStatus && typeof latestProviderKeyStatus === "object" ? latestProviderKeyStatus : {});
      setExecutionPolicy(latestExecutionPolicy && typeof latestExecutionPolicy === "object" ? latestExecutionPolicy : {});
      setEmailAlertPolicy(latestEmailAlertPolicy && typeof latestEmailAlertPolicy === "object" ? latestEmailAlertPolicy : {});
      setRotationPolicy(latestRotationPolicy && typeof latestRotationPolicy === "object" ? latestRotationPolicy : {});
      setProviderBudgetPolicy(
        latestProviderBudgetPolicy && typeof latestProviderBudgetPolicy === "object"
          ? latestProviderBudgetPolicy
          : {}
      );
      setSleevePolicy(latestSleevePolicy && typeof latestSleevePolicy === "object" ? latestSleevePolicy : {});
      setAutonomousExecPolicy(latestAutonomousExecPolicy && typeof latestAutonomousExecPolicy === "object" ? latestAutonomousExecPolicy : {});
      setSuccess(
        auditVisibilityEnabled
          ? "Settings saved successfully. Audit visibility: enabled."
          : "Settings saved successfully."
      );
    } catch (err) {
      if (err?.source === "provider_budget_policy" && Number(err?.status) === 400) {
        setError(
          err?.message ||
            "Provider budget policy validation failed. Please review provider budget fields and try again."
        );
        return;
      }
      if (err?.source === "sleeve_policy" && Number(err?.status) === 422) {
        setError(err?.message || "Sleeve policy validation failed. Check that long + short targets do not exceed 100% and write-off loss is negative.");
        return;
      }
      setError("Failed to save settings. Please try again.");
    }
  };

  const handleRiskPolicyChange = (e) => {
    const nextValue = e.target.value;
    setRiskPolicy({ ...safeRiskPolicy, [e.target.name]: nextValue });
    if (String(nextValue).trim() === "") {
      setInputErrors((prev) => ({ ...prev, [e.target.name]: "Risk policy value cannot be empty." }));
    } else {
      setInputErrors((prev) => {
        const { [e.target.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleBrokerSyncPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setBrokerSyncPolicy({ ...safeBrokerSyncPolicy, [e.target.name]: nextValue });
    if (typeof nextValue !== "boolean" && String(nextValue).trim() === "") {
      setInputErrors((prev) => ({ ...prev, [e.target.name]: "Broker sync policy value cannot be empty." }));
    } else {
      setInputErrors((prev) => {
        const { [e.target.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleReadinessAlertPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setReadinessAlertPolicy({ ...safeReadinessAlertPolicy, [e.target.name]: nextValue });
    if (e.target.name !== "webhook_url" && String(nextValue).trim() === "") {
      setInputErrors((prev) => ({ ...prev, [e.target.name]: "Alert policy value cannot be empty." }));
    } else {
      setInputErrors((prev) => {
        const { [e.target.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleReadinessActionPolicyChange = (e) => {
    const nextValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setReadinessActionPolicy({ ...safeReadinessActionPolicy, [e.target.name]: nextValue });
    if (String(nextValue).trim() === "") {
      setInputErrors((prev) => ({ ...prev, [e.target.name]: "Readiness action policy value cannot be empty." }));
    } else {
      setInputErrors((prev) => {
        const { [e.target.name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleRunBinancePreflight = async () => {
    if (!hasBrokerSync) {
      setBinancePreflightResult({
        status: "error",
        provider: "binance",
        message: "TradePilot Pro or above is required for broker sync preflight actions.",
        error_type: "capability_guard",
        checks: {},
      });
      return;
    }
    setBinancePreflightLoading(true);
    try {
      const timeout = Number(safeBrokerSyncPolicy.timeout_seconds ?? 6);
      const payload = await runBinancePreflight({ timeout, recv_window: 5000 });
      setBinancePreflightResult(payload && typeof payload === "object" ? payload : null);
    } catch (err) {
      setBinancePreflightResult({
        status: "error",
        provider: "binance",
        message: err?.message || "Failed to run Binance preflight",
        error_type: "client_error",
        checks: {},
      });
    } finally {
      setBinancePreflightLoading(false);
    }
  };

  const refreshAlpacaLiveArmStatus = async () => {
    try {
      const payload = await getAlpacaLiveArmStatus();
      setAlpacaLiveArmStatus(payload && typeof payload === "object" ? payload : null);
    } catch {
      setAlpacaLiveArmStatus(null);
    }
  };

  const handleRunAlpacaPreflight = async () => {
    if (!hasBrokerSync) {
      setAlpacaPreflightResult({
        status: "error",
        provider: "alpaca",
        message: "TradePilot Pro or above is required for broker sync preflight actions.",
        error_type: "capability_guard",
        checks: {},
      });
      return;
    }
    setAlpacaPreflightLoading(true);
    try {
      const timeout = Number(safeExecutionPolicy.order_timeout_seconds ?? 6);
      const base_url = String(safeExecutionPolicy.alpaca_base_url || "https://paper-api.alpaca.markets");
      const payload = await runAlpacaPreflight({ timeout, base_url });
      setAlpacaPreflightResult(payload && typeof payload === "object" ? payload : null);
    } catch (err) {
      setAlpacaPreflightResult({
        status: "error",
        provider: "alpaca",
        message: err?.message || "Failed to run Alpaca preflight",
        error_type: "client_error",
        checks: {},
      });
    } finally {
      setAlpacaPreflightLoading(false);
    }
  };

  const handleArmAlpacaLive = async () => {
    setAlpacaLiveArmLoading(true);
    try {
      const payload = await armAlpacaLiveTrading({
        passphrase: alpacaLiveArmPassphrase,
        ttl_minutes: Number(alpacaLiveArmTtl || 15),
      });
      setSuccess(`Live arm activated for ${payload?.ttl_minutes || alpacaLiveArmTtl} minute(s).`);
      setAlpacaLiveArmPassphrase("");
      await refreshAlpacaLiveArmStatus();
    } catch (err) {
      setError(err?.message || "Failed to arm Alpaca live mode.");
    } finally {
      setAlpacaLiveArmLoading(false);
    }
  };

  const handleRunGuardedDryOrder = async () => {
    if (!hasPaperTrading) {
      setAlpacaGuardedResult({
        status: "error",
        message: "TradePilot Pro or above is required for guarded dry-run order actions.",
      });
      return;
    }
    setAlpacaGuardedLoading(true);
    try {
      const payload = await submitAlpacaGuardedOrder({
        ticker: String(alpacaGuardedTicker || "AAPL").toUpperCase(),
        qty: Number(alpacaGuardedQty || 1),
        side: alpacaGuardedSide,
        order_type: String(safeExecutionPolicy.order_type || "market"),
        time_in_force: String(safeExecutionPolicy.time_in_force || "day"),
        timeout: Number(safeExecutionPolicy.order_timeout_seconds ?? 6),
        base_url: String(safeExecutionPolicy.alpaca_base_url || "https://paper-api.alpaca.markets"),
        dry_run: true,
        require_preflight: true,
      });
      setAlpacaGuardedResult(payload && typeof payload === "object" ? payload : null);
      await refreshAlpacaLiveArmStatus();
    } catch (err) {
      setAlpacaGuardedResult({
        status: "error",
        message: err?.message || "Failed to run guarded dry-run order",
      });
    } finally {
      setAlpacaGuardedLoading(false);
    }
  };

  const handleProviderPlanChange = (providerName, nextPlan) => {
    setProviderBudgetPolicy({
      ...safeProviderBudgetPolicy,
      active_plan: {
        ...(safeProviderBudgetPolicy.active_plan || {}),
        [providerName]: String(nextPlan || "free").trim().toLowerCase(),
      },
    });
  };

  const handleProviderDailyCallsChange = (providerName, planKey, nextValue) => {
    const currentDailyCalls = safeProviderBudgetPolicy.daily_calls || {};
    const currentProvider = currentDailyCalls[providerName] || {};
    setProviderBudgetPolicy({
      ...safeProviderBudgetPolicy,
      daily_calls: {
        ...currentDailyCalls,
        [providerName]: {
          ...currentProvider,
          [planKey]: nextValue,
        },
      },
    });
  };

  const handleProviderBudgetWindowChange = (fieldName, nextValue) => {
    setProviderBudgetPolicy({
      ...safeProviderBudgetPolicy,
      market_window: {
        ...(safeProviderBudgetPolicy.market_window || {}),
        [fieldName]: nextValue,
      },
    });
  };

  const refreshCapabilitiesSnapshot = async () => {
    try {
      const [data, storeStatus] = await Promise.all([
        getCapabilities(),
        Promise.resolve().then(() => getEntitlementStoreStatus()).catch(() => null),
      ]);
      setCapabilitySnapshot(data && typeof data === "object" ? data : null);
      const normalizedStore = storeStatus?.desktop_entitlement_store;
      setEntitlementStoreSnapshot(normalizedStore && typeof normalizedStore === "object" ? normalizedStore : null);
      window.dispatchEvent(new Event("tradepilot-entitlement-updated"));
    } catch {
      setCapabilitySnapshot(null);
      setEntitlementStoreSnapshot(null);
    }
  };

  const handleActivateLicense = async () => {
    setLicenseError("");
    setLicenseMessage("");

    const normalizedKey = String(licenseKey || "").trim();
    if (!normalizedKey) {
      setLicenseError("License key is required.");
      return;
    }

    setLicenseLoading(true);
    try {
      const result = await validateLicenseAndStoreEntitlement({
        license_key: normalizedKey,
        product_family: "app",
      });
      await refreshCapabilitiesSnapshot();
      const tierLabel = result?.tier_label || "TradePilot";
      setLicenseMessage(`License activated. Current entitlement: ${tierLabel}.`);
      setLicenseKey("");
    } catch (err) {
      setLicenseError(err?.response?.data?.detail || err?.message || "License activation failed.");
    } finally {
      setLicenseLoading(false);
    }
  };

  const handleClearLicense = async () => {
    setLicenseError("");
    setLicenseMessage("");
    await clearStoredEntitlement();
    await refreshCapabilitiesSnapshot();
    setLicenseMessage("Stored entitlement token cleared.");
  };

  const handleRecheckSecureStore = async () => {
    setLicenseError("");
    await refreshCapabilitiesSnapshot();
    setLicenseMessage("Secure-store status refreshed.");
  };

  const orderedProviderKeys = [
    ...PROVIDER_KEY_FIELDS,
    ...Object.keys(safeProviderKeys)
      .filter((key) => !PROVIDER_KEY_FIELDS.some((field) => field.name === key))
      .sort()
      .map((key) => ({
        name: key,
        label: key,
        helperText: "Provider credential",
      })),
  ];

  const getProviderStatusBadge = (providerName) => {
    const configured = Boolean(safeProviderKeyStatus[providerName]?.configured);
    return (
      <Chip
        size="small"
        label={configured ? "Configured" : "Missing"}
        color={configured ? "success" : "warning"}
        variant={configured ? "filled" : "outlined"}
      />
    );
  };

  const providerBudgetProviders = Array.from(
    new Set([
      ...Object.keys(safeProviderBudgetPolicy.active_plan || {}),
      ...Object.keys(safeProviderBudgetPolicy.daily_calls || {}),
    ])
  ).sort();

  const formatTimestamp = (value) => {
    if (!value) {
      return "Unknown";
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
  };

  return (
    <ResponsiveContainer>
      <Card elevation={3} sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontSize: { xs: 22, sm: 28 } }}>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Update API keys and app behavior settings.
          </Typography>

          {loading && <Typography>Loading settings...</Typography>}

          {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!loading && success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {!loading && (
            <Box component="form" onSubmit={handleSave}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Operational Baseline</Typography>
                <Typography variant="body2">
                  {safeAppSurfacePolicy.disclaimer_not_financial_advice || "TradePilot is for informational purposes only and does not constitute financial advice."}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Alerts should route to {safeOpsBaseline.alert_email_default || "admin@trytradepilot.com"}. Monitor {safeMonitoringBaseline.healthcheck_path || "/health"} every {safeMonitoringBaseline.expected_check_interval_minutes || 5} minutes, review {safeMonitoringBaseline.readiness_alerts_path || "/portfolio/readiness-alerts"}, and run {safeMonitoringBaseline.readiness_alert_test_path || "/portfolio/readiness-alerts/test"} after deployment changes.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Legal surfaces: {safeLegalLinks.terms_of_service || "https://trytradepilot.com/terms"} | {safeLegalLinks.privacy_policy || "https://trytradepilot.com/privacy"} | {safeLegalLinks.risk_disclosure || "https://trytradepilot.com/risk-disclosure"}
                </Typography>
              </Alert>

              <Typography variant="h6" mb={1}>Broker Credentials</Typography>
              <Stack spacing={1.5} mb={3}>
                {Object.keys(safeProviderKeys).length > 0 ? (
                  orderedProviderKeys
                    .filter((field) => field.name === "alpaca" || field.name === "alpaca_secret" || field.section === "email")
                    .map((field) => (
                      <Stack key={field.name} spacing={0.75}>
                        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                          {getProviderStatusBadge(field.name)}
                        </Stack>
                        <TextField
                          label={field.label}
                          type="password"
                          name={field.name}
                          value={safeProviderKeys[field.name] ?? ""}
                          onChange={handleKeyChange}
                          autoComplete="off"
                          error={Boolean(inputErrors[field.name])}
                          helperText={inputErrors[field.name] || field.helperText}
                          size="small"
                        />
                      </Stack>
                    ))
                ) : (
                  <Typography color="text.secondary">No provider keys found.</Typography>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>Data Provider API Keys</Typography>
              <Stack spacing={1.5} mb={3}>
                {Object.keys(safeProviderKeys).length > 0 ? (
                  orderedProviderKeys
                    .filter((field) => field.name !== "alpaca" && field.name !== "alpaca_secret" && field.section !== "email")
                    .map((field) => (
                      <Stack key={field.name} spacing={0.75}>
                        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                          {getProviderStatusBadge(field.name)}
                        </Stack>
                        <TextField
                          label={field.label}
                          type="password"
                          name={field.name}
                          value={safeProviderKeys[field.name] ?? ""}
                          onChange={handleKeyChange}
                          autoComplete="off"
                          error={Boolean(inputErrors[field.name])}
                          helperText={inputErrors[field.name] || field.helperText}
                          size="small"
                        />
                      </Stack>
                    ))
                ) : (
                  <Typography color="text.secondary">No provider keys found.</Typography>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>General Settings</Typography>
              <Stack spacing={1.5} mb={3}>
                {Object.keys(safeSettings).length > 0 ? (
                  Object.entries(safeSettings).map(([key, value]) => (
                    <TextField
                      key={key}
                      label={key}
                      type="text"
                      name={key}
                      value={value}
                      onChange={handleSettingsChange}
                      error={Boolean(inputErrors[key])}
                      helperText={inputErrors[key] || " "}
                      size="small"
                    />
                  ))
                ) : (
                  <Typography color="text.secondary">No general settings found.</Typography>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>Risk Policy Thresholds</Typography>
              <Stack spacing={1.5} mb={3}>
                {Object.keys(safeRiskPolicy).length > 0 ? (
                  Object.entries(safeRiskPolicy).map(([key, value]) => (
                    <TextField
                      key={key}
                      label={key}
                      type="number"
                      name={key}
                      value={value}
                      onChange={handleRiskPolicyChange}
                      error={Boolean(inputErrors[key])}
                      helperText={inputErrors[key] || " "}
                      size="small"
                    />
                  ))
                ) : (
                  <Typography color="text.secondary">No risk policy values found.</Typography>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>Readiness Alert Policy</Typography>
              <Stack spacing={1.5} mb={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      name="enabled"
                      checked={Boolean(safeReadinessAlertPolicy.enabled)}
                      onChange={handleReadinessAlertPolicyChange}
                    />
                  )}
                  label="Enable readiness alerts"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      name="emit_on_recovery"
                      checked={Boolean(safeReadinessAlertPolicy.emit_on_recovery)}
                      onChange={handleReadinessAlertPolicyChange}
                    />
                  )}
                  label="Emit recovery alerts"
                />
                <TextField
                  label="min_repeat_minutes"
                  type="number"
                  name="min_repeat_minutes"
                  value={safeReadinessAlertPolicy.min_repeat_minutes ?? ""}
                  onChange={handleReadinessAlertPolicyChange}
                  error={Boolean(inputErrors.min_repeat_minutes)}
                  helperText={inputErrors.min_repeat_minutes || "Minimum minutes between repeated blocked alerts."}
                  size="small"
                />
                <TextField
                  label="webhook_url"
                  type="text"
                  name="webhook_url"
                  value={safeReadinessAlertPolicy.webhook_url ?? ""}
                  onChange={handleReadinessAlertPolicyChange}
                  error={Boolean(inputErrors.webhook_url)}
                  helperText={inputErrors.webhook_url || "Optional HTTPS endpoint for alert delivery."}
                  size="small"
                />
              </Stack>

              <Typography variant="h6" mb={1}>Broker Sync Policy</Typography>
              <Stack spacing={1.5} mb={3}>
                <TextField
                  label="provider"
                  select
                  name="provider"
                  value={safeBrokerSyncPolicy.provider ?? "alpaca"}
                  onChange={handleBrokerSyncPolicyChange}
                  error={Boolean(inputErrors.provider)}
                  helperText={inputErrors.provider || "Broker sync source provider."}
                  size="small"
                >
                  <MenuItem value="alpaca">alpaca</MenuItem>
                  <MenuItem value="paper">paper</MenuItem>
                  <MenuItem value="binance">binance</MenuItem>
                </TextField>
                <FormControlLabel
                  control={(
                    <Switch
                      name="binance_authenticated_enabled"
                      checked={Boolean(safeBrokerSyncPolicy.binance_authenticated_enabled)}
                      onChange={handleBrokerSyncPolicyChange}
                    />
                  )}
                  label="Enable authenticated Binance broker sync"
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                  Required for signed Binance account/trade sync and preflight account checks. Keep disabled unless Binance credentials are configured.
                </Typography>
                <TextField
                  label="sync_days"
                  type="number"
                  name="sync_days"
                  value={safeBrokerSyncPolicy.sync_days ?? ""}
                  onChange={handleBrokerSyncPolicyChange}
                  error={Boolean(inputErrors.sync_days)}
                  helperText={inputErrors.sync_days || "How many days of history to pull on each broker sync."}
                  size="small"
                />
                <TextField
                  label="retry_attempts"
                  type="number"
                  name="retry_attempts"
                  value={safeBrokerSyncPolicy.retry_attempts ?? ""}
                  onChange={handleBrokerSyncPolicyChange}
                  error={Boolean(inputErrors.retry_attempts)}
                  helperText={inputErrors.retry_attempts || "Max number of retry attempts on transient broker API errors."}
                  size="small"
                />
                <TextField
                  label="retry_backoff_seconds"
                  type="number"
                  name="retry_backoff_seconds"
                  value={safeBrokerSyncPolicy.retry_backoff_seconds ?? ""}
                  onChange={handleBrokerSyncPolicyChange}
                  error={Boolean(inputErrors.retry_backoff_seconds)}
                  helperText={inputErrors.retry_backoff_seconds || "Initial backoff between retry attempts (exponential)."}
                  size="small"
                />
                <TextField
                  label="min_interval_minutes"
                  type="number"
                  name="min_interval_minutes"
                  value={safeBrokerSyncPolicy.min_interval_minutes ?? ""}
                  onChange={handleBrokerSyncPolicyChange}
                  error={Boolean(inputErrors.min_interval_minutes)}
                  helperText={inputErrors.min_interval_minutes || "Minimum minutes between consecutive broker syncs."}
                  size="small"
                />
                <TextField
                  label="timeout_seconds"
                  type="number"
                  name="timeout_seconds"
                  value={safeBrokerSyncPolicy.timeout_seconds ?? ""}
                  onChange={handleBrokerSyncPolicyChange}
                  error={Boolean(inputErrors.timeout_seconds)}
                  helperText={inputErrors.timeout_seconds || "HTTP timeout for each broker API request."}
                  size="small"
                />
              </Stack>

              <Typography variant="h6" mb={1}>Readiness Action Policy</Typography>
              <Stack spacing={1.5} mb={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      name="enabled"
                      checked={Boolean(safeReadinessActionPolicy.enabled)}
                      onChange={handleReadinessActionPolicyChange}
                    />
                  )}
                  label="Enable readiness action hooks"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      name="dry_run"
                      checked={Boolean(safeReadinessActionPolicy.dry_run)}
                      onChange={handleReadinessActionPolicyChange}
                    />
                  )}
                  label="Dry-run only (no side effects)"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      name="on_blocked"
                      checked={Boolean(safeReadinessActionPolicy.on_blocked)}
                      onChange={handleReadinessActionPolicyChange}
                    />
                  )}
                  label="Trigger when readiness becomes blocked"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      name="on_recovered"
                      checked={Boolean(safeReadinessActionPolicy.on_recovered)}
                      onChange={handleReadinessActionPolicyChange}
                    />
                  )}
                  label="Trigger when readiness recovers"
                />
                <TextField
                  label="cooldown_minutes"
                  type="number"
                  name="cooldown_minutes"
                  value={safeReadinessActionPolicy.cooldown_minutes ?? ""}
                  onChange={handleReadinessActionPolicyChange}
                  error={Boolean(inputErrors.cooldown_minutes)}
                  helperText={inputErrors.cooldown_minutes || "Minimum minutes between action triggers."}
                  size="small"
                />
              </Stack>

              <Typography variant="h6" mb={1}>Broker Sync Status</Typography>
              {brokerSyncStatus ? (
                <Box mb={3} p={1.5} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Provider:</Typography>
                    <Chip size="small" label={brokerSyncStatus.provider || "unknown"} variant="outlined" />
                    <Chip
                      size="small"
                      label={brokerSyncStatus.synced ? "Synced" : "Not synced"}
                      color={brokerSyncStatus.synced ? "success" : "default"}
                    />
                    {brokerSyncStatus.hard_fail && (
                      <Chip size="small" label="Hard fail" color="error" />
                    )}
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Last sync: {brokerSyncStatus.as_of ? new Date(brokerSyncStatus.as_of).toLocaleString() : "Never"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Equity points: {brokerSyncStatus.equity?.points ?? 0} &nbsp;|&nbsp;
                      Trades imported: {brokerSyncStatus.trades?.trades ?? 0}
                    </Typography>
                    {(brokerSyncStatus.equity?.error || brokerSyncStatus.trades?.error) && (
                      <Typography variant="caption" color="error.main">
                        Error: {brokerSyncStatus.equity?.error || brokerSyncStatus.trades?.error}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" mb={3}>
                  No broker sync has run yet.
                </Typography>
              )}

              <Typography variant="h6" mb={1}>Portfolio Equity Seed Status</Typography>
              {portfolioSeedStatus ? (
                <Box mb={3} p={1.5} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={600}>Active source:</Typography>
                    <Chip
                      size="small"
                      label={String(portfolioSeedStatus.active_source || "unknown")}
                      color={portfolioSeedStatus.active_source === "runtime" ? "success" : "default"}
                      variant={portfolioSeedStatus.active_source === "runtime" ? "filled" : "outlined"}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`rows: ${Number(portfolioSeedStatus.row_count || 0)}`}
                    />
                  </Stack>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Active path: {portfolioSeedStatus.active_path || "in-memory-defaults"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last update: {formatTimestamp(portfolioSeedStatus.active_updated_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Runtime file: {portfolioSeedStatus.runtime_exists ? "present" : "missing"} ({portfolioSeedStatus.runtime_path || "runtime/portfolio_equity_seed.json"})
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Template file: {portfolioSeedStatus.template_exists ? "present" : "missing"} ({portfolioSeedStatus.template_path || "config/portfolio_equity_seed.json"})
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Portfolio equity seed status is unavailable.
                </Typography>
              )}

              <Typography variant="h6" mb={1}>Binance Preflight</Typography>
              <Stack spacing={1} mb={3}>
                <Typography variant="body2" color="text.secondary">
                  Run a read-only authenticated check for Binance account access and permissions before enabling live sync.
                </Typography>
                {!hasBrokerSync && (
                  <Alert severity="info">
                    Upgrade to Pro to unlock broker sync preflight actions.
                  </Alert>
                )}
                <Box>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleRunBinancePreflight}
                    disabled={!hasBrokerSync || binancePreflightLoading}
                  >
                    {binancePreflightLoading ? "Running Binance Preflight..." : "Run Binance Preflight"}
                  </Button>
                </Box>
                {binancePreflightResult && (
                  <Box p={1.5} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                      <Chip
                        size="small"
                        label={String(binancePreflightResult.status || "unknown").toUpperCase()}
                        color={
                          binancePreflightResult.status === "ok"
                            ? "success"
                            : binancePreflightResult.status === "warn"
                              ? "warning"
                              : "error"
                        }
                      />
                      <Chip
                        size="small"
                        label={`error_type: ${binancePreflightResult.error_type || "none"}`}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {binancePreflightResult.message || "No message returned"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Credentials configured: {binancePreflightResult?.checks?.credentials_configured ? "yes" : "no"}
                      {" | "}
                      Sync enabled: {binancePreflightResult?.checks?.authenticated_sync_enabled ? "yes" : "no"}
                      {" | "}
                      Account access: {binancePreflightResult?.checks?.account_access ? "yes" : "no"}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>Alpaca Safety Workflow</Typography>
              <Stack spacing={1.25} mb={3}>
                <Typography variant="body2" color="text.secondary">
                  Run preflight, arm live mode with short TTL, and dry-run guarded submit from one place.
                </Typography>
                {!hasBrokerSync && (
                  <Alert severity="info">
                    Upgrade to Pro to unlock Alpaca preflight actions.
                  </Alert>
                )}
                {!hasPaperTrading && (
                  <Alert severity="info">
                    Upgrade to Pro to unlock guarded dry-run order actions.
                  </Alert>
                )}
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    color={alpacaLiveArmStatus?.live_armed ? "success" : "default"}
                    label={alpacaLiveArmStatus?.live_armed ? "Live Arm: ACTIVE" : "Live Arm: INACTIVE"}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`TTL(s): ${Number(alpacaLiveArmStatus?.seconds_remaining || 0)}`}
                  />
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    onClick={refreshAlpacaLiveArmStatus}
                    disabled={!hasAutomationEngine}
                  >
                    Refresh Live Arm
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleRunAlpacaPreflight}
                    disabled={!hasBrokerSync || alpacaPreflightLoading}
                  >
                    {alpacaPreflightLoading ? "Running Alpaca Preflight..." : "Run Alpaca Preflight"}
                  </Button>
                </Stack>
                {alpacaPreflightResult && (
                  <Box p={1.5} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                      <Chip
                        size="small"
                        label={String(alpacaPreflightResult.status || "unknown").toUpperCase()}
                        color={
                          alpacaPreflightResult.status === "ok"
                            ? "success"
                            : alpacaPreflightResult.status === "warn"
                              ? "warning"
                              : "error"
                        }
                      />
                      <Chip size="small" variant="outlined" label={`error_type: ${alpacaPreflightResult.error_type || "none"}`} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {alpacaPreflightResult.message || "No message returned"}
                    </Typography>
                  </Box>
                )}
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    label="Live Arm Passphrase"
                    type="password"
                    size="small"
                    value={alpacaLiveArmPassphrase}
                    onChange={(e) => setAlpacaLiveArmPassphrase(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <TextField
                    label="TTL Minutes"
                    type="number"
                    size="small"
                    value={alpacaLiveArmTtl}
                    onChange={(e) => setAlpacaLiveArmTtl(Number(e.target.value || 15))}
                    sx={{ width: 120 }}
                  />
                  <Button
                    type="button"
                    variant="contained"
                    color="warning"
                    onClick={handleArmAlpacaLive}
                    disabled={!hasAutomationEngine || alpacaLiveArmLoading}
                  >
                    {alpacaLiveArmLoading ? "Arming Live..." : "Arm Live Mode"}
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    label="Ticker"
                    size="small"
                    value={alpacaGuardedTicker}
                    onChange={(e) => setAlpacaGuardedTicker(e.target.value.toUpperCase())}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    label="Qty"
                    size="small"
                    type="number"
                    value={alpacaGuardedQty}
                    onChange={(e) => setAlpacaGuardedQty(Number(e.target.value || 1))}
                    sx={{ width: 110 }}
                  />
                  <TextField
                    label="Side"
                    name="alpaca_guarded_side"
                    select
                    size="small"
                    value={alpacaGuardedSide}
                    onChange={(e) => setAlpacaGuardedSide(e.target.value)}
                    sx={{ width: 120 }}
                  >
                    <MenuItem value="buy">buy</MenuItem>
                    <MenuItem value="sell">sell</MenuItem>
                  </TextField>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleRunGuardedDryOrder}
                    disabled={!hasPaperTrading || alpacaGuardedLoading}
                  >
                    {alpacaGuardedLoading ? "Running Guarded Dry-Run..." : "Run Guarded Dry-Run"}
                  </Button>
                </Stack>
                {alpacaGuardedResult && (
                  <Box p={1.5} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status: {String(alpacaGuardedResult.status || "unknown").toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Dry run: {alpacaGuardedResult?.dry_run ? "yes" : "no"}
                      {" | "}
                      Order result: {alpacaGuardedResult?.result?.status || "n/a"}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>Execution Policy</Typography>
              <Stack spacing={1.5} mb={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      name="paper_mode"
                      checked={Boolean(safeExecutionPolicy.paper_mode ?? true)}
                      onChange={handleExecutionPolicyChange}
                    />
                  )}
                  label="Paper trading mode (use Alpaca paper endpoint)"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      name="dry_run"
                      checked={Boolean(safeExecutionPolicy.dry_run ?? true)}
                      onChange={handleExecutionPolicyChange}
                    />
                  )}
                  label="Dry-run orders (validate only, never submit live)"
                />
                <TextField
                  label="Alpaca Base URL"
                  name="alpaca_base_url"
                  value={safeExecutionPolicy.alpaca_base_url ?? "https://paper-api.alpaca.markets"}
                  onChange={handleExecutionPolicyChange}
                  helperText="Paper: https://paper-api.alpaca.markets  |  Live: https://api.alpaca.markets"
                  size="small"
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Order Type"
                    name="order_type"
                    value={safeExecutionPolicy.order_type ?? "market"}
                    onChange={handleExecutionPolicyChange}
                    helperText="market or limit"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Time in Force"
                    name="time_in_force"
                    value={safeExecutionPolicy.time_in_force ?? "day"}
                    onChange={handleExecutionPolicyChange}
                    helperText="day, gtc, ioc, fok"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Order Timeout (s)"
                    name="order_timeout_seconds"
                    type="number"
                    value={safeExecutionPolicy.order_timeout_seconds ?? 6}
                    onChange={handleExecutionPolicyChange}
                    helperText="HTTP timeout per order request"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Stack>

              <Typography variant="h6" mb={1}>Email Alert Settings</Typography>
              <Stack spacing={1.5} mb={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      name="enabled"
                      checked={Boolean(safeEmailAlertPolicy.enabled)}
                      onChange={handleEmailAlertPolicyChange}
                    />
                  )}
                  label="Enable email alerts"
                />
                <TextField
                  label="SMTP User (sender email)"
                  name="smtp_user"
                  value={safeEmailAlertPolicy.smtp_user ?? ""}
                  onChange={handleEmailAlertPolicyChange}
                  helperText="Gmail address used as the sender (e.g. alerts@gmail.com)"
                  size="small"
                />
                <TextField
                  label="Alert Recipient (to address)"
                  name="smtp_to_address"
                  value={safeEmailAlertPolicy.smtp_to_address ?? ""}
                  onChange={handleEmailAlertPolicyChange}
                  helperText="Email address to receive signal alerts. Default admin route: admin@trytradepilot.com"
                  size="small"
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="SMTP Host"
                    name="smtp_host"
                    value={safeEmailAlertPolicy.smtp_host ?? "smtp.gmail.com"}
                    onChange={handleEmailAlertPolicyChange}
                    helperText="Default: smtp.gmail.com"
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label="SMTP Port"
                    name="smtp_port"
                    type="number"
                    value={safeEmailAlertPolicy.smtp_port ?? 587}
                    onChange={handleEmailAlertPolicyChange}
                    helperText="587 (STARTTLS)"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Gmail app password is stored in the SMTP / Gmail App Password field under Broker Credentials.
                </Typography>
              </Stack>

              <Typography variant="h6" mb={1}>Portfolio Rotation Policy</Typography>
              <Stack spacing={1.5} mb={3}>
                <FormControlLabel
                  control={(
                    <Switch
                      name="enable_rotation"
                      checked={Boolean(safeRotationPolicy.enable_rotation ?? true)}
                      onChange={handleRotationPolicyChange}
                    />
                  )}
                  label="Enable portfolio rotation"
                />
                <FormControlLabel
                  control={(
                    <Switch
                      name="profit_taking_enabled"
                      checked={Boolean(safeRotationPolicy.profit_taking_enabled)}
                      onChange={handleRotationPolicyChange}
                    />
                  )}
                  label="Enable profit-taking (deploy new capital when above target)"
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Min Candidate Score"
                    name="min_candidate_score"
                    type="number"
                    value={safeRotationPolicy.min_candidate_score ?? 55}
                    onChange={handleRotationPolicyChange}
                    helperText="Minimum signal score to consider for rotation in"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Min Score Edge"
                    name="min_score_edge"
                    type="number"
                    value={safeRotationPolicy.min_score_edge ?? 15}
                    onChange={handleRotationPolicyChange}
                    helperText="Score advantage required over the weakest holding"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Positions"
                    name="max_positions"
                    type="number"
                    value={safeRotationPolicy.max_positions ?? 20}
                    onChange={handleRotationPolicyChange}
                    helperText="Maximum number of concurrent positions"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
              </Stack>

              <Typography variant="h6" mb={1}>Provider Budget Policy</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Configure free vs paid plan pacing and per-provider daily API allowance used by distribution scheduling.
              </Typography>
              <Stack spacing={1.5} mb={3}>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Market Window Start"
                    name="budget_start"
                    value={safeProviderBudgetPolicy.market_window?.start ?? "09:30"}
                    onChange={(e) => handleProviderBudgetWindowChange("start", e.target.value)}
                    helperText="HH:MM format"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Market Window End"
                    name="budget_end"
                    value={safeProviderBudgetPolicy.market_window?.end ?? "16:00"}
                    onChange={(e) => handleProviderBudgetWindowChange("end", e.target.value)}
                    helperText="HH:MM format"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Market Window Timezone"
                    name="budget_timezone"
                    value={safeProviderBudgetPolicy.market_window?.timezone ?? "America/New_York"}
                    onChange={(e) => handleProviderBudgetWindowChange("timezone", e.target.value)}
                    helperText="IANA timezone"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>

                {providerBudgetProviders.length > 0 ? (
                  providerBudgetProviders.map((providerName) => {
                    const currentPlan = safeProviderBudgetPolicy.active_plan?.[providerName] ?? "free";
                    const providerCalls = safeProviderBudgetPolicy.daily_calls?.[providerName] || {};
                    return (
                      <Stack key={providerName} spacing={1} sx={{ border: "1px solid", borderColor: "divider", p: 1.25, borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{providerName}</Typography>
                        <Stack direction="row" spacing={1.5}>
                          <TextField
                            label="Active Plan"
                            name={`plan_${providerName}`}
                            select
                            value={currentPlan}
                            onChange={(e) => handleProviderPlanChange(providerName, e.target.value)}
                            error={Boolean(inputErrors[`plan_${providerName}`])}
                            helperText={inputErrors[`plan_${providerName}`] || "Select provider tier"}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            <MenuItem value="free">free</MenuItem>
                            <MenuItem value="paid">paid</MenuItem>
                          </TextField>
                          <TextField
                            label="Free Daily Calls"
                            name={`daily_calls_${providerName}_free`}
                            type="number"
                            value={providerCalls.free ?? ""}
                            onChange={(e) => handleProviderDailyCallsChange(providerName, "free", e.target.value)}
                            error={Boolean(inputErrors[`daily_calls_${providerName}_free`])}
                            helperText={inputErrors[`daily_calls_${providerName}_free`] || "Minimum 1"}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="Paid Daily Calls"
                            name={`daily_calls_${providerName}_paid`}
                            type="number"
                            value={providerCalls.paid ?? ""}
                            onChange={(e) => handleProviderDailyCallsChange(providerName, "paid", e.target.value)}
                            error={Boolean(inputErrors[`daily_calls_${providerName}_paid`])}
                            helperText={inputErrors[`daily_calls_${providerName}_paid`] || "Minimum 1"}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                        </Stack>
                      </Stack>
                    );
                  })
                ) : (
                  <Typography color="text.secondary">No provider budget policy values found.</Typography>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>License & Tier</Typography>
              <Stack spacing={1.25} mb={3}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <Chip size="small" label={`Active: ${activeTierLabel}`} color="primary" variant="outlined" />
                  <Chip size="small" label={`Base: ${baseTierLabel}`} variant="outlined" />
                  <Chip
                    size="small"
                    label={entitlementApplied ? "Entitlement Applied" : "No Entitlement Token"}
                    color={entitlementApplied ? "success" : "default"}
                    variant={entitlementApplied ? "filled" : "outlined"}
                  />
                  <Chip size="small" label={`Secure Store: ${entitlementStoreMode}`} variant="outlined" />
                  <Chip
                    size="small"
                    label={entitlementStoreTokenPresent ? "Desktop Token Present" : "Desktop Token Missing"}
                    color={entitlementStoreTokenPresent ? "success" : "default"}
                    variant={entitlementStoreTokenPresent ? "filled" : "outlined"}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {secureStoreHelperText}
                </Typography>

                <TextField
                  label="TradePilot License Key"
                  name="tradepilot_license_key"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  size="small"
                  placeholder="TP-PRO-..."
                  helperText="Use a TP-STARTER / TP-ADV / TP-PRO / TP-EXEC key for app entitlements."
                />

                {licenseError && <Alert severity="error">{licenseError}</Alert>}
                {licenseMessage && <Alert severity="success">{licenseMessage}</Alert>}

                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleActivateLicense}
                    disabled={licenseLoading}
                  >
                    {licenseLoading ? "Activating..." : "Activate License"}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleClearLicense}
                    disabled={licenseLoading}
                  >
                    Clear Entitlement
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleRecheckSecureStore}
                    disabled={licenseLoading}
                  >
                    Re-check Secure Store
                  </Button>
                </Stack>
              </Stack>

              <Typography variant="h6" mb={1}>Multi-Sleeve Portfolio Policy</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Configure long-term / short-term allocation targets, write-off thresholds, and minimum hold durations.
              </Typography>
              <Stack spacing={1.5} mb={3}>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Long-Term Target (%)"
                    name="long_term_target_pct"
                    type="number"
                    slotProps={{ htmlInput: { min: 0, max: 100, step: 5 } }}
                    value={Math.round((safeSleevePolicy.long_term_target_pct ?? 0.70) * 100)}
                    onChange={(e) =>
                      setSleevePolicy({ ...safeSleevePolicy, long_term_target_pct: Number(e.target.value) / 100 })
                    }
                    helperText="Portion of portfolio in long-term positions"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Short-Term Target (%)"
                    name="short_term_target_pct"
                    type="number"
                    slotProps={{ htmlInput: { min: 0, max: 100, step: 5 } }}
                    value={Math.round((safeSleevePolicy.short_term_target_pct ?? 0.30) * 100)}
                    onChange={(e) =>
                      setSleevePolicy({ ...safeSleevePolicy, short_term_target_pct: Number(e.target.value) / 100 })
                    }
                    helperText="Portion of portfolio in short-term positions"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Sleeve Tolerance (%)"
                    name="sleeve_tolerance_pct"
                    type="number"
                    slotProps={{ htmlInput: { min: 0, max: 50, step: 1 } }}
                    value={Math.round((safeSleevePolicy.sleeve_tolerance_pct ?? 0.15) * 100)}
                    onChange={(e) =>
                      setSleevePolicy({ ...safeSleevePolicy, sleeve_tolerance_pct: Number(e.target.value) / 100 })
                    }
                    helperText="Drift allowed before rebalance is triggered"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
                {(() => {
                  const lt = Number(safeSleevePolicy.long_term_target_pct ?? 0.70);
                  const st = Number(safeSleevePolicy.short_term_target_pct ?? 0.30);
                  return lt + st > 1.0 + 0.001 ? (
                    <Alert severity="warning">
                      Long-term + short-term targets sum to {Math.round((lt + st) * 100)}% — must not exceed 100%.
                    </Alert>
                  ) : null;
                })()}
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Min Hold Days (Long-Term)"
                    name="min_hold_days_long_term"
                    type="number"
                    slotProps={{ htmlInput: { min: 1 } }}
                    value={safeSleevePolicy.min_hold_days_long_term ?? 90}
                    onChange={handleSleevePolicyChange}
                    helperText="Minimum days before a long-term position can be exited"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Min Hold Days (Short-Term)"
                    name="min_hold_days_short_term"
                    type="number"
                    slotProps={{ htmlInput: { min: 1 } }}
                    value={safeSleevePolicy.min_hold_days_short_term ?? 30}
                    onChange={handleSleevePolicyChange}
                    helperText="Minimum days before a short-term position can be exited"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Write-Off Min Viable Score"
                    name="write_off_min_viable_score"
                    type="number"
                    slotProps={{ htmlInput: { min: 0, max: 100 } }}
                    value={safeSleevePolicy.write_off_min_viable_score ?? 30}
                    onChange={handleSleevePolicyChange}
                    helperText="Position must score below this to be considered for write-off"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Write-Off Confirmation Cycles"
                    name="write_off_confirmation_cycles"
                    type="number"
                    slotProps={{ htmlInput: { min: 1, max: 10 } }}
                    value={safeSleevePolicy.write_off_confirmation_cycles ?? 2}
                    onChange={handleSleevePolicyChange}
                    helperText="Consecutive cycles below floor before write-off triggers"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Write-Off Max Loss (%)"
                    name="write_off_max_loss_pct"
                    type="number"
                    slotProps={{ htmlInput: { max: -1, step: 1 } }}
                    value={safeSleevePolicy.write_off_max_loss_pct ?? -35}
                    onChange={handleSleevePolicyChange}
                    helperText="Maximum drawdown before forced write-off (must be negative)"
                    size="small"
                    sx={{ flex: 1 }}
                    error={Number(safeSleevePolicy.write_off_max_loss_pct ?? -35) > -15}
                  />
                </Stack>
                {Number(safeSleevePolicy.write_off_max_loss_pct ?? -35) > -15 && (
                  <Alert severity="warning">
                    Write-off max loss is set above -15% — positions may sustain large losses before write-off triggers. Consider -15 to -20% for capital protection.
                  </Alert>
                )}
              </Stack>

              <Typography variant="h6" mb={1}>Autonomous Engine Policy</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Configure autonomous exit and entry thresholds. Use the arm/disarm controls below to enable live execution.
              </Typography>
              {!hasAutomationEngine && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Upgrade to Executive to unlock autonomous engine policy and arm/disarm controls.
                </Alert>
              )}
              <Stack spacing={1.5} mb={3}>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        name="auto_exit_harvest"
                        checked={Boolean(safeAutonomousExecPolicy.auto_exit_harvest)}
                        onChange={handleAutonomousExecPolicyChange}
                        disabled={!hasAutomationEngine}
                      />
                    )}
                    label="Auto-exit: harvest profits"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        name="auto_exit_write_off"
                        checked={Boolean(safeAutonomousExecPolicy.auto_exit_write_off)}
                        onChange={handleAutonomousExecPolicyChange}
                        disabled={!hasAutomationEngine}
                      />
                    )}
                    label="Auto-exit: write-off losses"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        name="sleeve_mode"
                        checked={Boolean(safeAutonomousExecPolicy.sleeve_mode ?? true)}
                        onChange={handleAutonomousExecPolicyChange}
                        disabled={!hasAutomationEngine}
                      />
                    )}
                    label="Sleeve mode (classify entries by sleeve)"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        name="include_prices_for_exit_scoring"
                        checked={Boolean(safeAutonomousExecPolicy.include_prices_for_exit_scoring)}
                        onChange={handleAutonomousExecPolicyChange}
                        disabled={!hasAutomationEngine}
                      />
                    )}
                    label="Fetch live prices for exit scoring"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        name="notify_on_action"
                        checked={Boolean(safeAutonomousExecPolicy.notify_on_action)}
                        onChange={handleAutonomousExecPolicyChange}
                        disabled={!hasAutomationEngine}
                      />
                    )}
                    label="Email on each action"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        name="notify_on_run_summary"
                        checked={Boolean(safeAutonomousExecPolicy.notify_on_run_summary)}
                        onChange={handleAutonomousExecPolicyChange}
                        disabled={!hasAutomationEngine}
                      />
                    )}
                    label="Email run summary"
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Max Positions / Run"
                    name="max_positions_per_run"
                    type="number"
                    slotProps={{ htmlInput: { min: 1, max: 20 } }}
                    value={safeAutonomousExecPolicy.max_positions_per_run ?? 3}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="Max new entries per autonomous cycle"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Harvests / Run"
                    name="max_harvests_per_run"
                    type="number"
                    slotProps={{ htmlInput: { min: 1, max: 10 } }}
                    value={safeAutonomousExecPolicy.max_harvests_per_run ?? 2}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="Max profit-harvest exits per cycle"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Write-Offs / Run"
                    name="max_write_offs_per_run"
                    type="number"
                    slotProps={{ htmlInput: { min: 1, max: 10 } }}
                    value={safeAutonomousExecPolicy.max_write_offs_per_run ?? 2}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="Max write-off exits per cycle"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Min Score to Enter"
                    name="min_score_to_enter"
                    type="number"
                    slotProps={{ htmlInput: { min: 0, max: 100 } }}
                    value={safeAutonomousExecPolicy.min_score_to_enter ?? 40}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="Candidate composite score floor for new entries"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Max Position Size (%)"
                    name="max_position_size_pct"
                    type="number"
                    slotProps={{ htmlInput: { min: 1, max: 100 } }}
                    value={safeAutonomousExecPolicy.max_position_size_pct ?? 20}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="Maximum allocation per position as % of portfolio"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    select
                    label="Capital Source Mode"
                    name="capital_source_mode"
                    value={safeAutonomousExecPolicy.capital_source_mode || "max_available"}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="How autonomous sizing resolves available capital"
                    size="small"
                    sx={{ flex: 1 }}
                  >
                    <MenuItem value="max_available">Max available (broker vs reinvestment)</MenuItem>
                    <MenuItem value="broker_preferred">Broker preferred (fallback to reinvestment)</MenuItem>
                    <MenuItem value="broker_only">Broker only (fallback blocks live entries)</MenuItem>
                    <MenuItem value="reinvestment_only">Reinvestment pool only</MenuItem>
                  </TextField>
                  <TextField
                    label="Capital Safety Buffer (%)"
                    name="capital_safety_buffer_pct"
                    type="number"
                    slotProps={{ htmlInput: { min: 0, max: 100, step: 0.25 } }}
                    value={safeAutonomousExecPolicy.capital_safety_buffer_pct ?? 0}
                    onChange={handleAutonomousExecPolicyChange}
                    disabled={!hasAutomationEngine}
                    helperText="Reserve this percent of selected capital to avoid overextension"
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Stack>

                {/* Arm / Disarm panel — dedicated actions, not part of Save Settings */}
                <Box sx={{ border: "1px solid", borderColor: isArmed ? "warning.main" : "divider", borderRadius: 1, p: 1.5, mt: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>Engine State:</Typography>
                    <Chip
                      size="small"
                      label={isArmed ? "ARMED" : "DISARMED"}
                      color={isArmed ? "warning" : "default"}
                      variant={isArmed ? "filled" : "outlined"}
                    />
                    {safeAutonomousExecPolicy.armed_at && (
                      <Typography variant="caption" color="text.secondary">
                        since {new Date(safeAutonomousExecPolicy.armed_at).toLocaleString()}
                      </Typography>
                    )}
                  </Stack>
                  {safeAutonomousExecPolicy.armed_reason && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Reason: {safeAutonomousExecPolicy.armed_reason}
                    </Typography>
                  )}
                  <Alert severity={governanceLastRunError ? "error" : "info"} sx={{ mb: 1 }}>
                    <strong>Runtime status:</strong>{" "}
                    Last run: {governanceLastRunAt ? new Date(governanceLastRunAt).toLocaleString() : "Never"}.{" "}
                    Live runs: {governanceRunsLive}.{" "}
                    {governanceLastRunError ? `Last error: ${governanceLastRunError}` : "No recent run errors reported."}
                  </Alert>
                  {governanceUsageKnown && (
                    <Alert severity={governanceNearCapacity ? "warning" : "info"} sx={{ mb: 1 }}>
                      Governance log usage: {governanceRunsTotal}/{governanceRunCapacity} runs and {governanceActionsTotal}/{governanceActionCapacity} actions.
                      {governanceNearCapacity ? " Older log entries may roll off soon." : ""}
                    </Alert>
                  )}
                  <Box sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5, flexWrap: "wrap" }}>
                      <Typography variant="caption" color="text.secondary">
                        Last 5 autonomous actions
                      </Typography>
                      <TextField
                        select
                        size="small"
                        label="Action Filter"
                        value={actionsPreviewFilter}
                        onChange={(e) => setActionsPreviewFilter(e.target.value)}
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="entries">Entries</MenuItem>
                        <MenuItem value="exits">Exits</MenuItem>
                      </TextField>
                    </Stack>
                    {filteredActionsPreview.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No actions recorded for this filter.
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {filteredActionsPreview.map((action, idx) => (
                          <Typography key={`${action.ranAt}-${action.symbol}-${idx}`} variant="caption" color="text.secondary">
                            {action.ranAt ? new Date(action.ranAt).toLocaleString() : "Unknown time"} | {action.actionType || "unknown_action"}
                            {action.symbol ? ` ${action.symbol}` : ""}
                            {action.rationale ? ` — ${action.rationale}` : ""}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  {isArmed && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      Engine is ARMED — autonomous cycles will execute real trades if run in live mode.
                    </Alert>
                  )}
                  {!isArmed && (
                    <Stack spacing={1}>
                      <TextField
                        label="Arm Reason"
                        value={armReason}
                        onChange={(e) => setArmReason(e.target.value)}
                        placeholder="e.g. Paper testing run 2025-01"
                        size="small"
                        disabled={!hasAutomationEngine}
                        helperText="Required governance audit trail entry"
                      />
                      <Button
                        type="button"
                        variant="contained"
                        color="warning"
                        onClick={handleArmEngine}
                        disabled={!hasAutomationEngine || armLoading || !armReason.trim()}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {armLoading ? "Arming..." : "Arm Engine"}
                      </Button>
                    </Stack>
                  )}
                  {isArmed && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="warning"
                      onClick={handleDisarmEngine}
                      disabled={!hasAutomationEngine || armLoading}
                    >
                      {armLoading ? "Disarming..." : "Disarm Engine"}
                    </Button>
                  )}
                  {armError && <Alert severity="error" sx={{ mt: 1 }}>{armError}</Alert>}
                  {armSuccess && <Alert severity="success" sx={{ mt: 1 }}>{armSuccess}</Alert>}
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleExportGovernanceCsv}
                      disabled={!hasAutomationEngine || governanceExportLoading}
                    >
                      {governanceExportLoading ? "Exporting CSV..." : "Export Governance Log CSV"}
                    </Button>
                    {governanceExportSuccess && (
                      <Typography variant="caption" color="success.main">
                        {governanceExportSuccess}
                      </Typography>
                    )}
                  </Stack>
                  {governanceExportError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{governanceExportError}</Alert>
                  )}
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained">Save Settings</Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setRetryCount((c) => c + 1)}
                >
                  Reload
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
};

export default SettingsView;
